/**
 * Consumer Vault Service
 * API client for /api/vault/* — requires an authenticated consumer session.
 * See docs/features/CONSUMER-VAULT.md for the build contract.
 */
import { getAuthHeaders } from './authService';
import type { PublicBrand } from '../components/shopper/Brand';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '';

// sessionStorage key used to stash a "save this product" intent across the
// consumer auth redirect (product page -> sign in/up -> back to product page).
export const VAULT_INTENT_KEY = 'basiqr_vault_intent';

// What the brand wrote about a piece, sorted by role (snapshot v2). Brands
// name their fields freely ("Aftercare", "Washing"…); the server maps them.
export interface VaultPieceDetails {
  price?: string;
  colour?: string;
  size?: string;
  fibre?: string;
  weight?: string;
  origin?: string;
  care?: string[];
  repairs?: string;
  story?: string; // a short excerpt of the brand's description
}

// Mirrors VaultItemSnapshot in backend/src/models/vault.model.ts — keep fields in sync.
export interface VaultItemSnapshot {
  name: string;
  imageUrl?: string;
  creatorName?: string;
  creatorVerified?: boolean;
  collectionName?: string;
  // v2 (absent on older rows until they are backfilled)
  v?: number;
  creatorId?: string;
  variant?: string; // items: "Ink · S"
  serial?: string; // items: the label's number, e.g. "MR-TE-0114"
  details?: VaultPieceDetails;
}

// 'own' = in their wardrobe, 'want' = on their wishlist. Absent = 'own'.
export type VaultIntent = 'own' | 'want';

// Mirrors VaultItem in backend/src/models/vault.model.ts — keep fields in sync.
export interface VaultItem {
  productId: string;
  productType: 'main' | 'sub';
  folderId: string | null;
  savedAt: string;
  intent?: VaultIntent;
  snapshot: VaultItemSnapshot;
  /** The brand's logos now (#462): looked up when the list loads, not saved. */
  brandLogos?: PublicBrand;
}

// Mirrors VaultFolder in backend/src/models/vault.model.ts — keep fields in sync.
export interface VaultFolder {
  folderId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface VaultMarketingConsent {
  optedIn: boolean;
  updatedAt: string;
  policyVersion: string;
  source: 'signup' | 'settings';
}

// Where brand news reaches the consumer. Absent = 'inbox'.
export type ContactChannel = 'inbox' | 'email' | 'both';

export interface VaultMe {
  accountType: 'client' | 'consumer';
  email: string;
  marketingConsent?: VaultMarketingConsent;
  counts?: { items: number; folders: number };
  limits?: { items: number; folders: number };
  contactChannel?: ContactChannel;
}

// A brand found in the consumer's vault, and whether it may contact them.
export interface VaultBrand {
  brandId: string;
  name: string;
  pieces: number;
  allowed: boolean;
  updatedAt?: string;
}

export interface VaultMessage {
  msgId: string;
  createdAt: string;
  from: { kind: 'basiqr' | 'brand'; brandId?: string; name: string };
  title: string;
  body: string;
  imageUrl?: string;
  cta?: { label: string; url: string };
  code?: string;
  readAt?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data?: T;
  code?: string;
  count?: number;
  nextToken?: string;
  unread?: number; // inbox list only
  brands?: Record<string, PublicBrand>; // item list only: logos by brand id (#462)
}

class VaultApiError extends Error {
  status?: number;
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'VaultApiError';
    this.status = status;
    this.code = code;
  }
}

// Server error codes whose `message` is written for the consumer to read.
const USER_FACING_CODES = new Set(['VAULT_ITEM_CAP_EXCEEDED', 'VAULT_FOLDER_CAP_EXCEEDED']);

/**
 * Text to show for a failed vault call: the server's own message for errors
 * the consumer can act on (a cap reached, rate limited), else `fallback` —
 * other messages (500s, network failures) aren't written for end users.
 */
export const vaultErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof VaultApiError && (err.status === 429 || USER_FACING_CODES.has(err.code ?? ''))) {
    return err.message;
  }
  return fallback;
};

async function vaultFetch<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/vault${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(init.headers || {}),
    },
  });

  let body: ApiEnvelope<T> | null = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    throw new VaultApiError(
      body?.message || `Vault request failed (${response.status})`,
      response.status,
      body?.code
    );
  }

  return body ?? { success: true };
}

/**
 * Save a product to the signed-in consumer's vault. Idempotent server-side:
 * saving it again answers "Already saved" and changes nothing.
 */
export const saveItem = async (
  productId: string,
  options: { folderId?: string | null; intent?: VaultIntent } = {}
): Promise<VaultItem | undefined> => {
  const body = await vaultFetch<VaultItem>('/items', {
    method: 'POST',
    body: JSON.stringify({
      productId,
      ...(options.folderId !== undefined ? { folderId: options.folderId } : {}),
      ...(options.intent ? { intent: options.intent } : {}),
    }),
  });
  return body.data;
};

/**
 * Change a saved item's folder (null = no folder) and/or Owned/Wishlist intent.
 */
export const updateItem = async (
  productId: string,
  changes: { folderId?: string | null; intent?: VaultIntent }
): Promise<VaultItem | undefined> => {
  const body = await vaultFetch<VaultItem>(`/items/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
  return body.data;
};

/**
 * Remove a product from the vault.
 */
export const removeItem = async (productId: string): Promise<void> => {
  await vaultFetch<void>(`/items/${encodeURIComponent(productId)}`, { method: 'DELETE' });
};

/**
 * Move a saved item to a different folder (or unfile it with `null`).
 */
export const moveItem = async (productId: string, folderId: string | null): Promise<void> => {
  await vaultFetch<void>(`/items/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ folderId }),
  });
};

/**
 * List saved items, optionally filtered by folder ('unfiled' for null-folder items).
 */
export const getItems = async (params?: {
  folderId?: string;
  limit?: number;
  nextToken?: string;
}): Promise<{ items: VaultItem[]; nextToken?: string }> => {
  const query = new URLSearchParams();
  if (params?.folderId) query.set('folderId', params.folderId);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.nextToken) query.set('nextToken', params.nextToken);
  const qs = query.toString();
  const body = await vaultFetch<VaultItem[]>(`/items${qs ? `?${qs}` : ''}`);
  const brands = body.brands ?? {};
  const items = (body.data || []).map(item => {
    const logos = item.snapshot?.creatorId ? brands[item.snapshot.creatorId] : undefined;
    return logos ? { ...item, brandLogos: logos } : item;
  });
  return { items, nextToken: body.nextToken };
};

export const createFolder = async (name: string): Promise<VaultFolder | undefined> => {
  const body = await vaultFetch<VaultFolder>('/folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  return body.data;
};

export const getFolders = async (): Promise<VaultFolder[]> => {
  const body = await vaultFetch<VaultFolder[]>('/folders');
  return body.data || [];
};

export const renameFolder = async (folderId: string, name: string): Promise<void> => {
  await vaultFetch<void>(`/folders/${encodeURIComponent(folderId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
};

export const deleteFolder = async (folderId: string): Promise<void> => {
  await vaultFetch<void>(`/folders/${encodeURIComponent(folderId)}`, { method: 'DELETE' });
};

export const getVaultMe = async (): Promise<VaultMe | undefined> => {
  const body = await vaultFetch<VaultMe>('/me');
  return body.data;
};

/**
 * Update marketing-consent opt-in. Server stamps updatedAt/policyVersion.
 * Pass source 'signup' for the consent given on the signup form (GDPR
 * provenance); defaults to 'settings' for toggles from the vault settings.
 */
export const updateConsent = async (
  optedIn: boolean,
  source: 'signup' | 'settings' = 'settings'
): Promise<void> => {
  await vaultFetch<void>('/consent', {
    method: 'PATCH',
    body: JSON.stringify({ optedIn, source }),
  });
};

/**
 * Right-to-erasure: deletes all vault rows, the Dynamo user row, and the
 * Cognito user. Consumer accounts only.
 */
export const deleteAccount = async (): Promise<void> => {
  await vaultFetch<void>('/account', { method: 'DELETE' });
};

// ---- Brands that may contact the consumer --------------------------------

export const getBrands = async (): Promise<VaultBrand[]> => {
  const body = await vaultFetch<VaultBrand[]>('/brands');
  return body.data || [];
};

/**
 * Allow or stop one brand's news. `source` records where the choice was made
 * (consent provenance): the save sheet, settings, or the sign-up form.
 */
export const setBrandAllowed = async (
  brandId: string,
  allowed: boolean,
  source: 'save' | 'settings' | 'signup' = 'settings'
): Promise<void> => {
  await vaultFetch<void>(`/brands/${encodeURIComponent(brandId)}`, {
    method: 'PUT',
    body: JSON.stringify({ allowed, source }),
  });
};

export const updatePreferences = async (prefs: { contactChannel: ContactChannel }): Promise<void> => {
  await vaultFetch<void>('/preferences', {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });
};

// ---- Inbox ------------------------------------------------------------------

export const getInbox = async (params?: {
  limit?: number;
  nextToken?: string;
}): Promise<{ messages: VaultMessage[]; unread: number; nextToken?: string }> => {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.nextToken) query.set('nextToken', params.nextToken);
  const qs = query.toString();
  const body = await vaultFetch<VaultMessage[]>(`/inbox${qs ? `?${qs}` : ''}`);
  return { messages: body.data || [], unread: body.unread ?? 0, nextToken: body.nextToken };
};

export const markMessageRead = async (msgId: string, read = true): Promise<void> => {
  await vaultFetch<void>(`/inbox/${encodeURIComponent(msgId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ read }),
  });
};

export const markAllMessagesRead = async (): Promise<void> => {
  await vaultFetch<void>('/inbox/read-all', { method: 'POST' });
};

export const deleteMessage = async (msgId: string): Promise<void> => {
  await vaultFetch<void>(`/inbox/${encodeURIComponent(msgId)}`, { method: 'DELETE' });
};

export { VaultApiError };
